import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminSiteSettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    site_title: "",
    hero_heading: "",
    hero_subtitle: "",
    contact_phone: "",
    contact_email: "",
    contact_address: "",
  });
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/api/admin/site-settings");
        if (data) {
          setForm({
            site_title: data.site_title || "",
            hero_heading: data.hero_heading || "",
            hero_subtitle: data.hero_subtitle || "",
            contact_phone: data.contact_phone || "",
            contact_email: data.contact_email || "",
            contact_address: data.contact_address || "",
          });
          setLogoUrl(data.logo_url || "");
        }
        setErr("");
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.message || "Failed to load site settings");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErr("");

      // 1) Save text fields
      const { data } = await api.put("/api/admin/site-settings", form);
      setLogoUrl(data.logo_url || "");

      // 2) If logo file selected, upload
      if (logoFile) {
        const fd = new FormData();
        fd.append("logo", logoFile);
        const { data: logoRes } = await api.post(
          "/api/admin/site-settings/logo",
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        setLogoUrl(logoRes.logo_url || "");
        setLogoFile(null);
      }

      alert("Settings saved");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f8fafc" },
    container: { maxWidth: 960, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 },
    input: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    textarea: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
      minHeight: 80,
    },
    buttonPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    buttonGhost: {
      background: "#e2e8f0",
      color: "#0f172a",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
    },
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />
      <div style={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            Site Settings (Home Page)
          </h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>

        <div style={styles.card}>
          {loading && <div>Loading…</div>}
          {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}

          {!loading && (
            <form onSubmit={onSubmit}>
              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={styles.label}>Site Title</label>
                  <input
                    style={styles.input}
                    name="site_title"
                    value={form.site_title}
                    onChange={onChange}
                  />
                </div>
                <div>
                  <label style={styles.label}>Hero Heading</label>
                  <input
                    style={styles.input}
                    name="hero_heading"
                    value={form.hero_heading}
                    onChange={onChange}
                  />
                </div>
                <div>
                  <label style={styles.label}>Hero Subtitle</label>
                  <input
                    style={styles.input}
                    name="hero_subtitle"
                    value={form.hero_subtitle}
                    onChange={onChange}
                  />
                </div>

                <div>
                  <label style={styles.label}>Contact Phone</label>
                  <input
                    style={styles.input}
                    name="contact_phone"
                    value={form.contact_phone}
                    onChange={onChange}
                  />
                </div>
                <div>
                  <label style={styles.label}>Contact Email</label>
                  <input
                    style={styles.input}
                    name="contact_email"
                    value={form.contact_email}
                    onChange={onChange}
                  />
                </div>
                <div>
                  <label style={styles.label}>Contact Address</label>
                  <textarea
                    style={styles.textarea}
                    name="contact_address"
                    value={form.contact_address}
                    onChange={onChange}
                  />
                </div>

                <div>
                  <label style={styles.label}>Logo</label>
                  {logoUrl && (
                    <div style={{ marginBottom: 8 }}>
                      <img
                        src={logoUrl}
                        alt="Current logo"
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 12,
                          objectFit: "cover",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setLogoFile(file || null);
                    }}
                  />
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                    Max 2 MB, PNG/JPG.
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <button
                  type="submit"
                  style={styles.buttonPrimary}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save Settings"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
